<template>
  <div>
    <div
      class="
        login-form
        tw-border-solid
        tw-border
        tw-border-purellGray
        tw-mb-6
        tw-mt-20
        tw-p-4
        tw-bg-white
        tw-rounded
        tw-w-full
        tw-max-w-xl
        tw-m-auto
      "
    >
      <login-logo />

      <div class="tw-mb-6">
        <h1 class="tw-text-lg tw-font-bold tw-mb-3">
          {{ $t("log-in.reset-password") }}
        </h1>
        <p class="tw-text-base">
          {{ $t("log-in.reset-enter-email") }}
        </p>
      </div>

      <div class="tw-mb-6">
        <div
          v-if="errorMessage"
          class="tw-text-md tw-font-bold tw-mb-0 tw-text-red-500"
        >
          {{ errorMessage }}
        </div>
      </div>

      <v-snackbar
        @input="showEmailSentMessage = false"
        :value="showEmailSentMessage"
        color="primary"
        :top="true"
        :timeout="-1"
        class="tw-p-12"
      >
        <div class="tw-text-lg tw-mb-3">
          {{ $t("log-in.reset-instructions-sent") }}
        </div>
        <div>
          {{ $t("log-in.reset-instructions") }}
        </div>
      </v-snackbar>

      <ValidationObserver ref="form" v-slot="{ pristine }">
        <v-form @submit.prevent="submitPasswordReset">
          <ValidationProvider
            :name="$t('log-in.form.email-address')"
            :rules="{ required: true, email: true }"
            v-slot="{ errors }"
          >
            <v-text-field
              v-model="email"
              :error-messages="errors"
              name="email"
              id="email"
              value=""
              required
              :label="$t('log-in.form.email-address')"
              type="text"
              class="tw-mb-4"
              :disabled="loading || showEmailSentMessage"
              @input="errorMessage = ''"
            />
          </ValidationProvider>

          <v-btn
            :disabled="pristine || showEmailSentMessage || loading"
            :loading="loading"
            color="primary"
            block
            type="submit"
          >
            {{ $t("log-in.form.submit") }}
          </v-btn>
        </v-form>
      </ValidationObserver>
    </div>

    <div class="tw-flex tw-justify-center">
      <country-dropdown />
    </div>
  </div>
</template>

<script>
import LoginLogo from "@/views/login/components/Logo.vue";
import CountryDropdown from "@/components/ui/CountryDropdown.vue";
import i18n from "@/plugins/i18n";
import { scrollToFirstError } from "@/utils/scrollToFirstError";

export default {
  components: {
    LoginLogo,
    CountryDropdown
  },
  data: () => ({
    email: "",
    showEmailSentMessage: false,
    loading: false,
    errorMessage: ""
  }),
  methods: {
    async submitPasswordReset() {
      this.errorMessage = "";

      const valid = await this.$refs.form.validate();

      if (!valid) {
        await this.$nextTick();
        scrollToFirstError(this.$el);
        return;
      }

      this.loading = true;

      try {
        const result = await this.forgotPasswordWithFetch(this.email);

        if (result.ok) {
          this.showEmailSentMessage = true;
          return;
        }

        this.handleHttpError(result.status, result.code);
      } catch (e) {
        this.errorMessage = i18n.t("log-in.internal-error");
      } finally {
        this.loading = false;
      }
    },

    async forgotPasswordWithFetch(email) {
      const response = await fetch("/api/ForgotPassword", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          Email: email
        })
      });

      if (response.ok) {
        return { ok: true, status: response.status, code: null };
      }

      let code = null;

      try {
        const ct = (response.headers.get("content-type") || "").toLowerCase();
        if (ct.includes("application/json")) {
          const data = await response.json();
          code = data && data.code ? String(data.code) : null;
        }
      } catch (e) {
        code = null;
      }

      return { ok: false, status: response.status, code };
    },

    handleHttpError(status) {
      // Enumeration-safe UI: do not surface specific account state in the browser.
      if (status >= 500) {
        this.errorMessage = i18n.t("log-in.internal-error");
        return;
      }
      // For most failures (including unknown email, SSO-only account, etc.), show the generic "email sent" snackbar.
      this.showEmailSentMessage = true;
    }
  }
};
</script>
